import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { toast } from 'react-hot-toast';

/**
 * Email Verification Component
 * 📧 Handles email verification with a code after registration
 */
const EmailVerification = () => {
  const navigate = useNavigate();
  const { user, verifyEmail, resendVerificationCode, error, loading } = useAuthContext();
  const [verificationCode, setVerificationCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Auto-fill the email from the user context
  const userEmail = user?.email || '';

  // Handle countdown for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && resendDisabled) {
      setResendDisabled(false);
    }
  }, [countdown, resendDisabled]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !verificationCode || verificationCode.length !== 6) return;

    try {
      setIsSubmitting(true);
      await verifyEmail({ email: userEmail, code: verificationCode });
      
      // More detailed success toast
      toast.success('Email verified successfully! You are now logged in.');
      
      // Allow the toast to be visible briefly before redirect
      setTimeout(() => {
        // Use replace: true to prevent going back to verification page with browser back button
        navigate('/dashboard', { replace: true });
      }, 1000);
    } catch (err) {
      // More specific error message using error from context
      toast.error(error || 'Invalid verification code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (resendDisabled) return;

    try {
      setResendDisabled(true);
      setCountdown(60); // 60 second cooldown
      await resendVerificationCode(userEmail);
      toast.success('Verification code resent. Please check your email.');
    } catch (err) {
      toast.error(error || 'Failed to resend code. Please try again later.');
      setResendDisabled(false);
      setCountdown(0);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#6C5DD3] to-[#8247E5]">
      <div className="bg-white bg-opacity-95 backdrop-blur-sm p-8 rounded-3xl shadow-xl w-full max-w-md mx-4">
        <h2 className="text-2xl font-semibold text-center mb-2 text-[#6C5DD3]">Verify your email</h2>
        <p className="text-center text-gray-600 mb-8">
          We've sent a verification code to <span className="font-medium">{userEmail}</span>
        </p>

        {error && (
          <div className="mb-6 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="block text-gray-600 text-sm" htmlFor="verificationCode">
              Verification Code
            </label>
            <input
              id="verificationCode"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="6"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ''))}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent text-gray-900 text-sm tracking-widest text-center font-mono text-xl"
              placeholder="123456"
              required
              disabled={isSubmitting || loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the 6-digit code sent to your email
            </p>
          </div>

          <button
            type="submit"
            className="w-full bg-[#6C5DD3] text-white rounded-xl py-3 font-medium hover:bg-[#5849B6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSubmitting || loading || verificationCode.length !== 6}
          >
            {isSubmitting || loading ? 'Verifying...' : 'Verify Email'}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={handleResendCode}
              className="mt-4 py-2 px-6 bg-[#6C5DD3] text-white rounded-xl hover:bg-[#5849B6] transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={resendDisabled}
            >
              {resendDisabled
                ? `Resend code in ${countdown}s`
                : "Didn't receive a code? Resend"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailVerification; 